import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";
import api, { API_BASE } from "../lib/api";

const TOKEN_KEY = "pharm_auth_token";
const AuthContext = createContext(null);

// Install axios interceptors once.
api.interceptors.request.use((config) => {
  const t = localStorage.getItem(TOKEN_KEY);
  if (t && !config.headers?.Authorization) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${t}`;
  }
  return config;
});

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);   // { id, username, role }
  const [status, setStatus] = useState("loading"); // "loading" | "authed" | "anon"

  const loadMe = useCallback(async () => {
    const t = localStorage.getItem(TOKEN_KEY);
    if (!t) {
      setStatus("anon");
      setUser(null);
      return;
    }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      setStatus("authed");
    } catch {
      localStorage.removeItem(TOKEN_KEY);
      setUser(null);
      setStatus("anon");
    }
  }, []);

  useEffect(() => {
    loadMe();
  }, [loadMe]);

  // Install response interceptor that redirects to /login on 401 for app routes.
  useEffect(() => {
    const id = api.interceptors.response.use(
      (r) => r,
      (error) => {
        if (error?.response?.status === 401) {
          const url = error.config?.url || "";
          if (!url.includes("/auth/login") && !url.includes("/auth/me")) {
            localStorage.removeItem(TOKEN_KEY);
            setUser(null);
            setStatus("anon");
          }
        }
        return Promise.reject(error);
      }
    );
    return () => api.interceptors.response.eject(id);
  }, []);

  const login = useCallback(async (username, password) => {
    // Raw axios call (don't use `api` to avoid infinite redirect loop on 401).
    const { data } = await axios.post(
      `${API_BASE}/auth/login`,
      { username, password },
      { timeout: 15000 }
    );
    localStorage.setItem(TOKEN_KEY, data.token);
    setUser(data.user);
    setStatus("authed");
    return data.user;
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    setUser(null);
    setStatus("anon");
  }, []);

  // Forcefully revoke ALL existing tokens for the current user (this device + others).
  // Useful after a suspected token leak / lost device.
  const logoutAllDevices = useCallback(async () => {
    try {
      await api.post("/auth/logout-all");
    } catch {
      // even if the server call fails (e.g., we're already revoked), clean up locally
    }
    localStorage.removeItem(TOKEN_KEY);
    setUser(null);
    setStatus("anon");
  }, []);

  const value = useMemo(
    () => ({ user, status, login, logout, logoutAllDevices, refresh: loadMe }),
    [user, status, login, logout, logoutAllDevices, loadMe]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth outside AuthProvider");
  return ctx;
}
